import { useTranslation } from "react-i18next";
import { Icon, MEDIA_BACKDROP_HOST_CLASS, MediaBackdrop, MediaPreview, cn } from "@nexus/ui";
import type { PreflightReportAsset } from "../../types/preflight.types";

function isVideo(contentType: string | null): boolean {
  return contentType?.startsWith("video/") ?? false;
}

export interface ReportAssetPreviewProps {
  asset: PreflightReportAsset;
  label: string;
}

/** Clickable report-asset preview — opens MediaPreview lightbox when a signed asset URL exists. */
export function ReportAssetPreview({ asset, label }: ReportAssetPreviewProps) {
  const { t } = useTranslation();
  const video = isVideo(asset.contentType);
  const mediaSrc = asset.assetUrl ?? undefined;
  const thumbnailSrc = asset.thumbnailUrl ?? asset.assetUrl ?? undefined;

  if (!thumbnailSrc) {
    return (
      <div className="bg-muted text-muted-foreground flex aspect-video w-full flex-col items-center justify-center gap-2 overflow-hidden rounded-md">
        <Icon name={video ? "clapperboard" : "file"} size="lg" aria-hidden />
        <span className="sr-only">
          {t("preflight.creativeCard.noPreview", { name: label })}
        </span>
      </div>
    );
  }

  return (
    <MediaPreview
      src={mediaSrc}
      thumbnailSrc={thumbnailSrc}
      type={video ? "video" : "image"}
      alt={label}
      title={label}
      className={cn(
        MEDIA_BACKDROP_HOST_CLASS,
        "aspect-video rounded-md [&_img:not([data-slot=media-backdrop])]:object-contain [&_video]:object-contain",
      )}
    >
      {/* A video with no thumbnail fails in MediaPreview's <img> and its fallback covers the box,
          so a backdrop there would never show. */}
      <MediaBackdrop src={asset.thumbnailUrl ?? (video ? null : asset.assetUrl)} />
    </MediaPreview>
  );
}
